import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import PlaceIcon from '@mui/icons-material/Place';
import StarIcon from '@mui/icons-material/Star';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import LanguageIcon from '@mui/icons-material/Language';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { formatDurationHuman } from '../../utils';
import type { SegmentCardBaseProps } from '../../types';

interface SegmentCardPOIProps extends SegmentCardBaseProps {
  poiNumber: number | null;
  onCardClick: () => void;
}

function SegmentCardPOI({
  segment,
  poiNumber,
  isEditMode,
  isBeingDragged,
  shouldGrayOut,
  isFirst,
  isLast,
  onMoveUp,
  onMoveDown,
  onCardClick,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragEnter,
  onDragLeave,
  onDrop,
  onDelete,
}: SegmentCardPOIProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Card
      draggable={isEditMode && !isMobile}
      onDragStart={(e) => isEditMode && !isMobile && onDragStart(e)}
      onDragEnd={(e) => isEditMode && !isMobile && onDragEnd(e)}
      onDragOver={(e) => isEditMode && !isMobile && onDragOver(e)}
      onDragEnter={(e) => isEditMode && !isMobile && onDragEnter(e)}
      onDragLeave={(e) => isEditMode && !isMobile && onDragLeave(e)}
      onDrop={(e) => isEditMode && !isMobile && onDrop(e)}
      onClick={(e) => {
        if (!isEditMode) {
          onCardClick();
        }
      }}
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: 'stretch',
        boxShadow: 1,
        cursor: isEditMode && !isMobile ? 'move' : isEditMode ? 'default' : 'pointer',
        position: 'relative',
        transition: 'all 0.2s ease-in-out',
        borderWidth: 2,
        borderStyle: 'solid',
        borderColor: 'transparent',
        backgroundColor: isBeingDragged ? 'action.hover' : 'background.paper',
        opacity: shouldGrayOut ? 0.4 : 1,
        filter: shouldGrayOut ? 'grayscale(50%)' : 'none',
        '&:hover': {
          boxShadow: 2,
          transform: isEditMode ? 'none' : 'translateY(-1px)',
        }
      }}
    >
      {/* Image Section */}
      <Box sx={{
        width: { xs: '100%', sm: 200 },
        minWidth: { xs: '100%', sm: 200 },
        height: { xs: 150, sm: 180 },
        position: 'relative',
        overflow: 'hidden'
      }}>
        {segment.mainImage ? (
          <img
            src={segment.mainImage}
            alt={segment.name || 'POI'}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'grey.100'
            }}
          >
            <PlaceIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          </Box>
        )}

        {/* Number Badge */}
        {poiNumber && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              width: 32,
              height: 32,
              borderRadius: '50%',
              backgroundColor: 'primary.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              fontSize: '14px',
              boxShadow: 2,
              zIndex: 1
            }}
          >
            {poiNumber}
          </Box>
        )}
      </Box>

      {/* Content Section */}
      <Box sx={{
        flex: 1,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}>
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              mb: 1,
              color: 'text.primary'
            }}
          >
            {segment.name || segment.title || segment.point_of_interest || segment.description || 'POI'}
          </Typography>

          {segment.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1, lineHeight: 1.5 }}
            >
              {segment.description}
            </Typography>
          )}

          {segment.address && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mb: 1
              }}
            >
              <PlaceIcon sx={{ fontSize: 16 }} />
              {segment.address}
            </Typography>
          )}

          {/* POI Metadata */}
          {segment.fullDetails && (
            <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              {(segment.fullDetails.poi_type || segment.fullDetails.type || segment.type) && (
                <Chip
                  label={segment.fullDetails.poi_type || segment.fullDetails.type || segment.type}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem' }}
                />
              )}

              {segment.fullDetails.rating && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <StarIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {segment.fullDetails.rating}
                  </Typography>
                </Box>
              )}

              {segment.fullDetails.time_required_in_minutes && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AccessTimeIcon sx={{ fontSize: 16, color: 'action.active' }} />
                  <Typography variant="body2" color="text.secondary">
                    {formatDurationHuman(segment.fullDetails.time_required_in_minutes)}
                  </Typography>
                </Box>
              )}

              {segment.fullDetails.predicted_cost_usd > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                  <AttachMoneyIcon sx={{ fontSize: 16, color: 'action.active' }} />
                  <Typography variant="body2" color="text.secondary">
                    {segment.fullDetails.predicted_cost_usd}
                  </Typography>
                </Box>
              )}

              {segment.fullDetails.tour_available === true && (
                <Chip
                  label="Tour Available"
                  size="small"
                  color="secondary"
                  sx={{ fontSize: '0.7rem' }}
                />
              )}

              {segment.fullDetails.website && (
                <IconButton
                  component="a"
                  href={segment.fullDetails.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    color: 'grey.500',
                    minWidth: { xs: 44, sm: 'auto' },
                    minHeight: { xs: 44, sm: 'auto' },
                    '&:hover': {
                      color: 'primary.main',
                      backgroundColor: 'transparent'
                    }
                  }}
                >
                  <LanguageIcon sx={{ fontSize: { xs: 22, sm: 18 } }} />
                </IconButton>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Edit Mode: Drag Indicator (desktop) / Up-Down Arrows (mobile) and Delete Button */}
      {isEditMode && (
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'row', sm: 'column' },
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: 2, sm: 1 },
          px: 2,
          py: { xs: 1.5, sm: 0 },
          width: { xs: '100%', sm: 'auto' },
          borderTop: { xs: '1px solid', sm: 'none' },
          borderLeft: { xs: 'none', sm: '1px solid' },
          borderColor: 'divider',
          bgcolor: { xs: 'grey.50', sm: 'transparent' }
        }}>
          {/* Desktop: Drag indicator */}
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.5 }}>
            <DragIndicatorIcon
              sx={{
                color: 'action.active',
                fontSize: 28,
                cursor: 'move'
              }}
            />
          </Box>

          {/* Mobile: Up/Down arrows */}
          <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onMoveUp();
              }}
              disabled={isFirst}
              sx={{
                color: isFirst ? 'action.disabled' : 'action.active',
                minWidth: 44,
                minHeight: 44,
                backgroundColor: 'grey.100',
                '&:hover': {
                  bgcolor: 'grey.200',
                },
                '&.Mui-disabled': {
                  backgroundColor: 'grey.50',
                }
              }}
            >
              <KeyboardArrowUpIcon />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onMoveDown();
              }}
              disabled={isLast}
              sx={{
                color: isLast ? 'action.disabled' : 'action.active',
                minWidth: 44,
                minHeight: 44,
                backgroundColor: 'grey.100',
                '&:hover': {
                  bgcolor: 'grey.200',
                },
                '&.Mui-disabled': {
                  backgroundColor: 'grey.50',
                }
              }}
            >
              <KeyboardArrowDownIcon />
            </IconButton>
          </Box>

          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            sx={{
              color: 'action.active',
              backgroundColor: { xs: 'grey.100', sm: 'transparent' },
              minWidth: 44,
              minHeight: 44,
              '&:hover': {
                bgcolor: { xs: 'grey.200', sm: 'transparent' },
                color: 'grey.700'
              }
            }}
          >
            <DeleteOutlineIcon />
          </IconButton>
        </Box>
      )}
    </Card>
  );
}

export default SegmentCardPOI;
